/**
 * Feedback Manager
 * Manages user feedback functionality including card actions and email reports
 */

import { CardActionsComponent, EmailReportButtonComponent } from '../components';
import { StorageService } from '../services';

/**
 * Manager for user feedback features
 * Coordinates card actions and email report functionality
 */
export class FeedbackManager {
  private cardActionsComponent: CardActionsComponent;
  private emailReportButton: EmailReportButtonComponent;

  constructor() {
    this.cardActionsComponent = new CardActionsComponent();
    this.emailReportButton = new EmailReportButtonComponent(
      this.cardActionsComponent.getStorageService()
    );

    this.setupCallbacks();
  }

  /**
   * Setup internal callbacks between components
   */
  private setupCallbacks(): void {
    // Update email button when actions change
    this.cardActionsComponent.onChange((hasActions) => {
      this.emailReportButton.updateVisibility(hasActions);
    });

    // Clear card visual states when feedback is cleared
    this.emailReportButton.onFeedbackCleared(() => {
      this.clearCardActionStates();
    });
  }

  /**
   * Initialize the feedback manager with audit name
   * @param auditName - The current audit name for namespaced storage
   */
  initialize(auditName: string): void {
    // Set audit name for namespaced storage
    this.cardActionsComponent.getStorageService().setAuditName(auditName);
    this.emailReportButton.setAuditName(auditName);

    // Render and attach email report button to DOM
    const emailButtonElement = this.emailReportButton.render();
    document.body.appendChild(emailButtonElement);
  }

  /**
   * Get the card actions component
   * @returns CardActionsComponent instance
   */
  getCardActionsComponent(): CardActionsComponent {
    return this.cardActionsComponent;
  }

  /**
   * Get the storage service
   * @returns StorageService instance
   */
  getStorageService(): StorageService {
    return this.cardActionsComponent.getStorageService();
  }

  /**
   * Attach card action handlers to a container
   * @param container - Container element with cards
   */
  attachCardActionHandlers(container: HTMLElement): void {
    this.cardActionsComponent.attachHandlers(container, (button) => {
      const card = button.closest('[data-area]') as HTMLElement;
      if (!card) {
        return { area: '', recommendation: '' };
      }

      return {
        area: card.dataset.area || '',
        recommendation: card.dataset.recommendation || ''
      };
    });
  }

  /**
   * Update the email report button content
   */
  updateEmailReportButton(): void {
    this.emailReportButton.updateContent();
  }

  /**
   * Clear all card action button visual states
   */
  private clearCardActionStates(): void {
    const actionButtons = document.querySelectorAll<HTMLButtonElement>('.card-action-btn');
    actionButtons.forEach(button => {
      button.classList.remove(
        'bg-green-900/50', 'border-green-500', 'text-green-400',
        'bg-red-900/50', 'border-red-500', 'text-red-400',
        'bg-blue-900/50', 'border-blue-500', 'text-blue-400',
        'bg-yellow-900/50', 'border-yellow-500', 'text-yellow-400',
        'ring-2', 'ring-offset-1', 'ring-offset-gray-800'
      );
      button.setAttribute('aria-pressed', 'false');
    });
  }

  /**
   * Check if any feedback actions exist
   * @returns True if feedback exists
   */
  hasFeedback(): boolean {
    return this.cardActionsComponent.hasActions();
  }
}
